/// <amd-module name="Wit/SprintsHub/SprintsView/Services/CapacityService" />
import { ITeamSettings } from "Agile/Contracts/Common";
import { GlobalMessageLevel, IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { TeamContext } from "Tfs/Platform/Contracts/Core";
import { getCoreClient } from "Tfs/Platform/RestClient/Core";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IIdentity } from "VSSUI/IdentityPicker";
import { arrayEquals } from "VSSUI/Util";
import { DateRange, TeamMemberCapacityIdentityRef } from "Wit/Clients/Work/Contracts/Work";
import { getWorkClient } from "Wit/Clients/Work/RestClient/Work";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { shiftToLocal, shiftToUTC } from "Wit/Common/DateUtils";
import { getBoardsMessagesService, getErrorMessage } from "Wit/Messages/BoardsMessagesService";
import { ICapacity, ISprintCapacityOptions, IUserCapacity } from "Wit/SprintsHub/Common/Contracts/Interfaces";
import { renderDirtyButtonWrapper } from "Wit/SprintsHub/SprintsView/Components/Capacity/CapacityTab";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";
import { renderAddUserDialog } from "../Components/Capacity/AddUserDialog";
import { CapacityRevisionActions, ICapacityService, IUserActivity } from "./ICapacityService";

const CAPACITYOPTIONS_DATAPROVIDER_ID = "ms.vss-work-web.sprints-hub-capacityoptions-data-provider";
const TEAMCAPACITY_DATAPROVIDER_ID: string = "ms.vss-work-web.sprints-hub-capacity-data-provider";

interface IMemberCapacity extends IUserActivity {
    isTeamDaysOff?: boolean;
}

class CapacityService extends VssService implements ICapacityService {
    private memberCapacitiesObservable = new ObservableArray<IMemberCapacity>();
    private capacityChangeObservable = new ObservableValue<number>(0);
    private capacityOptions: ISprintCapacityOptions;

    private dirty = new ObservableValue<boolean>(false);
    private waiting = new ObservableValue<boolean>(false);

    private originalCapacityData: IUserCapacity[];
    private originalTeamDaysOff: readonly DateRange[];
    private teamDaysOffObservable = new ObservableArray<DateRange>();

    private prevDataParams: { teamId: string; projectId: string; iterationId: string };

    private isCapacityDataProviderEnabled: boolean;

    public _serviceStart(pageContext: IVssPageContext) {
        this.pageContext = pageContext;
        this.isCapacityDataProviderEnabled = !isFeatureFlagEnabled(pageContext, FeatureFlags.UseCapacityDataProviderDisabled, false);
    }

    public addActivity(activityRow: IUserActivity, index: number): void {
        this.memberCapacitiesObservable.splice(index + 1, 0, {
            activityName: new ObservableValue(""),
            capacityPerDay: new ObservableValue("0"),
            _links: activityRow._links,
            teamMember: activityRow.teamMember,
            url: activityRow.url
        });
        this.onUpdate();
    }

    public async addTeamMembers(teamId: string, projectId: string) {
        this.waiting.value = true;
        const teamInfo = await getCoreClient(this.pageContext).getTeamMembersWithExtendedProperties(projectId, teamId);

        const startLength = this.memberCapacitiesObservable.length;

        const newTeamMembers = teamInfo
            .filter(
                teamMember =>
                    !teamMember.identity.isContainer &&
                    !this.memberCapacitiesObservable.value.some(activity => activity.teamMember?.id === teamMember.identity.id)
            )
            .map(teamMember => {
                return {
                    activityName: new ObservableValue(""),
                    capacityPerDay: new ObservableValue("0"),
                    daysOff: new ObservableArray([]),
                    firstIndexOfUser: true,
                    _links: null,
                    teamMember: teamMember.identity,
                    url: ""
                };
            });

        const newCapacities = this.memberCapacitiesObservable.value.concat(newTeamMembers).sort(memberCapacityComparer);

        this.memberCapacitiesObservable.value = newCapacities;

        if (startLength === this.memberCapacitiesObservable.length) {
            this.pageContext
                .getService<IGlobalMessagesService>("IGlobalMessagesService")
                .addBanner({ message: Resources.NoMoreTeamMembers, level: GlobalMessageLevel.warning });
        }

        this.onUpdate();
        this.waiting.value = false;
    }

    public addUser(identity: IIdentity) {
        if (!identity.localId) {
            this.pageContext
                .getService<IGlobalMessagesService>("IGlobalMessagesService")
                .addBanner({ message: Resources.UnableToAddUserCapacity, level: GlobalMessageLevel.error });
            return;
        }
        const memberCapacities = [...this.memberCapacitiesObservable.value];
        memberCapacities.push({
            activityName: new ObservableValue(""),
            capacityPerDay: new ObservableValue("0"),
            daysOff: new ObservableArray([]),
            _links: null,
            teamMember: {
                descriptor: identity.subjectDescriptor,
                displayName: identity.displayName,
                id: identity.localId,
                uniqueName: identity.signInAddress,
                imageUrl: identity.image
            } as IdentityRef,
            url: ""
        });
        this.memberCapacitiesObservable.value = memberCapacities.sort(memberCapacityComparer);
        this.onUpdate();
    }

    private async copyFromLastSprint(teamId: string, projectId: string, iterationId: string) {
        this.waiting.value = true;

        const workRestClient = getWorkClient(this.pageContext);

        const teamContext: TeamContext = { teamId: teamId, team: "", projectId: projectId, project: "" };

        const prevSprintData = await workRestClient.getCapacitiesWithIdentityRefAndTotals(teamContext, iterationId);

        this.updateCapacityObservable(convertToIUserCapacity(prevSprintData.teamMembers), [], false);
        this.waiting.value = false;
    }

    public getCapacityCommandBarItems(teamSettings: ITeamSettings, iterationId: string): IHeaderCommandBarItem[] {
        let lastSprintId: string | undefined = undefined;

        if (!teamSettings || !teamSettings.teamId || !teamSettings.projectId || !iterationId) {
            return [];
        }

        const allSprints = [
            ...teamSettings.previousIterations,
            ...(teamSettings.currentIteration ? [teamSettings.currentIteration] : []),
            ...teamSettings.futureIterations
        ];
        const selectedSprintIndex = allSprints.findIndex(sprint => sprint.id === iterationId);
        if (selectedSprintIndex > 0) {
            lastSprintId = allSprints[selectedSprintIndex - 1].id;
        }

        const isNewSprintsViewEnabled = isFeatureFlagEnabled(this.pageContext, FeatureFlags.NewSprintsBacklogsView, false);

        return [
            {
                iconProps: { iconName: "Add" },
                id: "add-user",
                onActivate: () => {
                    const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                    layoutManager.renderCallout(onDismiss => renderAddUserDialog({ onDismiss }));
                },
                subtle: !isNewSprintsViewEnabled,
                isPrimary: isNewSprintsViewEnabled,
                text: Resources.AddUser
            },
            {
                iconProps: { iconName: "Save" },
                id: "save",
                important: true,
                onActivate: () => {
                    this.save(teamSettings.teamId!, teamSettings.projectId!, iterationId);
                },
                renderButton: buttonProps => renderDirtyButtonWrapper({ keyOverride: "save-capacity-button", buttonProps, dirty: this.dirty }),
                subtle: !isNewSprintsViewEnabled,
                text: Resources.Save
            },
            {
                iconProps: { iconName: "Undo" },
                id: "revert",
                onActivate: () => this.reset(),
                renderButton: buttonProps => renderDirtyButtonWrapper({ keyOverride: "revert-capacity-button", buttonProps, dirty: this.dirty }),
                subtle: !isNewSprintsViewEnabled,
                text: Resources.Revert
            },
            {
                iconProps: { iconName: "AddGroup" },
                id: "add-team-members",
                onActivate: () => {
                    this.addTeamMembers(teamSettings.teamId!, teamSettings.projectId!);
                },
                subtle: !isNewSprintsViewEnabled,
                text: Resources.AddTeamMembers
            },
            {
                disabled: lastSprintId === undefined,
                iconProps: { iconName: "Copy" },
                id: "copy-last-sprint",
                onActivate: () => {
                    this.copyFromLastSprint(teamSettings.teamId!, teamSettings.projectId!, lastSprintId!);
                },
                subtle: !isNewSprintsViewEnabled,
                text: Resources.CopyFromLastSprint
            }
        ];
    }

    public getCapacityOptions(): ISprintCapacityOptions | undefined {
        if (!this.capacityOptions) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            const options = contributionService.getData<ISprintCapacityOptions>(CAPACITYOPTIONS_DATAPROVIDER_ID);
            if (options) {
                this.capacityOptions = options;
            }
        }
        return this.capacityOptions;
    }

    public getChangeObservable(): IObservableValue<number> {
        return this.capacityChangeObservable;
    }

    public getMemberCapacities(): IObservableArray<IUserActivity> {
        return this.memberCapacitiesObservable;
    }

    public getWaitingObservable(): IObservableValue<boolean> {
        return this.waiting;
    }

    public getTeamDaysOff(): DateRange[] {
        return this.teamDaysOffObservable.value;
    }

    public getTeamDaysOffObservable(): ObservableArray<DateRange> {
        return this.teamDaysOffObservable;
    }

    public async loadCapacityData(teamId: string, projectId: string, iterationId: string): Promise<boolean> {
        // Multiple sources can try and load the data. Filter out duplicates if we have multiple of those open
        if (
            this.prevDataParams &&
            this.prevDataParams.teamId === teamId &&
            this.prevDataParams.projectId === projectId &&
            this.prevDataParams.iterationId === iterationId
        ) {
            return false;
        }

        this.prevDataParams = { teamId, projectId, iterationId };

        if (this.isCapacityDataProviderEnabled) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            let capacityData: ICapacity | undefined;
            try {
                capacityData = await contributionService.getDataAsync<ICapacity>(TEAMCAPACITY_DATAPROVIDER_ID, undefined, true);
            } catch (error) {
                this.showErrorMessage(format(Resources.CapacityLoadingError, error.message));
            }

            if (capacityData?.exceptionInfo) {
                this.showErrorMessage(format(Resources.CapacityLoadingError, capacityData.exceptionInfo.exceptionMessage));
            } else if (capacityData) {
                this.originalCapacityData = capacityData.userCapacities;
                this.originalTeamDaysOff = capacityData.teamDaysOff;
            }
        } else {
            const workRestClient = getWorkClient(this.pageContext);
            const teamContext: TeamContext = { teamId: teamId, team: "", projectId: projectId, project: "" };
            const capacityData = (await workRestClient.getCapacitiesWithIdentityRefAndTotals(teamContext, iterationId)).teamMembers;

            this.originalCapacityData = convertToIUserCapacity(capacityData);
            this.originalTeamDaysOff = (await workRestClient.getTeamDaysOff(teamContext, iterationId)).daysOff;
        }

        if (this.originalCapacityData) {
            this.originalCapacityData.forEach(teamMember => {
                teamMember.daysOff.forEach(this.shiftDateRangeToUTC);
            });
        }

        if (this.originalTeamDaysOff) {
            this.originalTeamDaysOff.forEach(this.shiftDateRangeToUTC);
        }
        this.updateCapacityObservable(this.originalCapacityData, this.originalTeamDaysOff, true);
        return true;
    }

    public removeActivity(index: number): void {
        this.memberCapacitiesObservable.splice(index, 1);
        this.onUpdate(CapacityRevisionActions.RemoveRow);
    }

    public removeUser(teamMemberId: string): void {
        this.memberCapacitiesObservable.removeAll(activity => activity.teamMember?.id === teamMemberId);
        this.onUpdate(CapacityRevisionActions.RemoveRow);
    }

    public reset() {
        this.updateCapacityObservable(this.originalCapacityData, this.originalTeamDaysOff, true);

        this.dirty.value = false;
    }

    public async save(teamId: string, projectId: string, iterationId: string): Promise<void> {
        this.waiting.value = true;

        const workRestClient = getWorkClient(this.pageContext);

        const teamContext: TeamContext = { teamId: teamId, team: "", projectId: projectId, project: "" };

        const updatedCapacityPayload: TeamMemberCapacityIdentityRef[] = [];
        const shiftDateRangeToLocal = (daysOff: DateRange) => {
            return {
                start: shiftToLocal(daysOff.start),
                end: shiftToLocal(daysOff.end)
            } as DateRange;
        };

        const updatedDaysOffPayload = this.teamDaysOffObservable.value.map(shiftDateRangeToLocal);
        this.dedupCapacitiesObservable();

        // Fill team daysoff payload container with new objects containing shifted dates (keep original team dates unchanged)
        this.memberCapacitiesObservable.value.forEach(capacity => {
            // Filter out team row
            if (!capacity.teamMember) {
                return;
            }

            const matchingCapacity = updatedCapacityPayload.find(updatedCapacity => updatedCapacity.teamMember.id === capacity.teamMember?.id);

            if (!matchingCapacity) {
                updatedCapacityPayload.push({
                    activities: [{ capacityPerDay: parseFloat(capacity.capacityPerDay.value) ?? 0, name: capacity.activityName.value }],
                    daysOff: capacity.daysOff?.value.map(shiftDateRangeToLocal) || [],
                    _links: capacity._links,
                    teamMember: capacity.teamMember,
                    url: capacity.url
                });
            } else {
                matchingCapacity.activities.push({
                    capacityPerDay: parseFloat(capacity.capacityPerDay.value) ?? 0,
                    name: capacity.activityName.value
                });
            }
        });

        this.originalTeamDaysOff = this.teamDaysOffObservable.value;

        let teamMembersCapacity: TeamMemberCapacityIdentityRef[] | undefined = undefined;
        try {
            teamMembersCapacity = await workRestClient.replaceCapacitiesWithIdentityRef(updatedCapacityPayload, teamContext, iterationId);
            await workRestClient.updateTeamDaysOff({ daysOff: updatedDaysOffPayload }, teamContext, iterationId);
        } catch (error) {
            this.showErrorMessage(error);
        }

        if (teamMembersCapacity) {
            teamMembersCapacity.forEach(teamMember => {
                teamMember.daysOff.forEach(this.shiftDateRangeToUTC);
            });

            this.originalCapacityData = convertToIUserCapacity(teamMembersCapacity);
        }

        this.waiting.value = false;
        this.dirty.value = false;
    }

    public updateActivity(userActivity: IUserActivity, activityName: string): void {
        userActivity.activityName.value = activityName;

        this.onUpdate(CapacityRevisionActions.Activity);
    }

    public updateCapacity(userActivity: IUserActivity, capacity: string): void {
        if (!Number.isNaN(parseFloat(capacity))) {
            userActivity.capacityPerDay.value = capacity;
            this.onUpdate(CapacityRevisionActions.Capacity);
        }
    }

    public updateDaysOff(userActivity: IUserActivity, daysOff?: DateRange[]): void {
        if (daysOff === undefined || !userActivity.daysOff) {
            return;
        }

        userActivity.daysOff.value = daysOff;

        this.onUpdate(CapacityRevisionActions.DaysOff);
    }

    private shiftDateRangeToUTC = (daysOff: DateRange) => {
        daysOff.start = shiftToUTC(daysOff.start);
        daysOff.end = shiftToUTC(daysOff.end);
    };

    private dedupCapacitiesObservable(): void {
        const getKey = (capacity: IMemberCapacity) => {
            return `${capacity.teamMember?.id || ""}_${capacity.activityName.value}`;
        };
        const activities = new Map<string, IMemberCapacity>();

        for (const capacity of this.memberCapacitiesObservable.value) {
            const key = getKey(capacity);
            if (activities.has(key)) {
                const capacitySum = parseFloat(capacity.capacityPerDay.value) + parseFloat(activities.get(key)!.capacityPerDay.value);
                activities.get(key)!.capacityPerDay.value = capacitySum.toString();
            } else {
                activities.set(key, capacity);
            }
        }
        this.memberCapacitiesObservable.value = Array.from(activities.values());
    }

    private async evaluateDirty() {
        // Any change in the total rows?
        if (
            this.memberCapacitiesObservable.length !==
            this.originalCapacityData.reduce((totalRows, capacity) => totalRows + capacity.activities.length, 0) + 1
        ) {
            this.dirty.value = true;
            return;
        }

        // Any changes in the individual values
        this.dirty.value = this.memberCapacitiesObservable.value.some(capacity => {
            let originalDaysOff: DateRange[];
            if (capacity.teamMember) {
                const matchingCapacity = this.originalCapacityData.find(
                    originalCapacityData => originalCapacityData.teamMember.id === capacity.teamMember?.id
                );

                if (!matchingCapacity) {
                    return true;
                }

                const matchingActivity = matchingCapacity.activities.find(activity => activity.name === capacity.activityName.value);

                if (!matchingActivity || matchingActivity.capacityPerDay.toString() !== capacity.capacityPerDay.value) {
                    return true;
                }

                originalDaysOff = matchingCapacity.daysOff;
            } else {
                // Team days off
                originalDaysOff = [...this.originalTeamDaysOff];
            }

            if (capacity.daysOff) {
                return !arrayEquals(capacity.daysOff.value, originalDaysOff, (dateRange1, dateRange2) => {
                    return dateRange1.start.getTime() === dateRange2.start.getTime() && dateRange1.end.getTime() === dateRange2.end.getTime();
                });
            }
            return false;
        });
    }

    private onUpdate(action?: CapacityRevisionActions) {
        this.evaluateDirty();
        if (action) {
            this.capacityChangeObservable.notify(0, action);
        }
    }

    private updateCapacityObservable(userCapacities: IUserCapacity[], teamDaysOff: readonly DateRange[], updateDaysOff: boolean) {
        const capacityData: IMemberCapacity[] = [];

        userCapacities.forEach(memberCapacity => {
            memberCapacity.activities.forEach((activity, index) => {
                let daysOff: DateRange[] | undefined = undefined;
                if (index === 0) {
                    if (updateDaysOff) {
                        // Make array's copy - keep the original array from possible changes
                        daysOff = [...memberCapacity.daysOff];
                    } else {
                        // Find and use the current value
                        daysOff =
                            this.memberCapacitiesObservable.value.find(
                                oldCaps => oldCaps.teamMember?.id === memberCapacity.teamMember.id && oldCaps.daysOff !== undefined
                            )?.daysOff?.value ?? [];
                    }
                }

                capacityData.push({
                    activityName: new ObservableValue(activity.name),
                    capacityPerDay: new ObservableValue(activity.capacityPerDay.toString()),
                    daysOff: daysOff !== undefined ? new ObservableArray(daysOff) : undefined,
                    _links: memberCapacity._links,
                    teamMember: memberCapacity.teamMemberIdentityRef,
                    url: memberCapacity.url
                });
            });
        });

        capacityData.push({
            activityName: new ObservableValue(""),
            capacityPerDay: new ObservableValue(""),
            daysOff: this.teamDaysOffObservable,
            isTeamDaysOff: true,
            _links: null,
            url: ""
        });

        if (updateDaysOff) {
            this.teamDaysOffObservable.value = [...teamDaysOff];
        }

        this.memberCapacitiesObservable.value = capacityData.sort(memberCapacityComparer);

        this.onUpdate(CapacityRevisionActions.Capacity);
    }

    private showErrorMessage(error: any) {
        const messagesService = getBoardsMessagesService(this.pageContext);
        messagesService.showMessage(getErrorMessage(error));
    }
}

function convertToIUserCapacity(teamMemberCapacities: TeamMemberCapacityIdentityRef[]): IUserCapacity[] {
    return teamMemberCapacities.map(teamMemberCapacity => ({
        teamMemberIdentityRef: teamMemberCapacity.teamMember,
        teamMember: teamMemberCapacity.teamMember,
        activities: teamMemberCapacity.activities,
        daysOff: teamMemberCapacity.daysOff.map(range => ({start: range.start, end: range.end, ratio: 1})),
        _links: teamMemberCapacity._links,
        url: teamMemberCapacity.url
    }));
}

const serviceName = "ICapacityService";
Services.add(serviceName, { serviceFactory: CapacityService });

function memberCapacityComparer(first: IMemberCapacity, second: IMemberCapacity) {
    if (first.isTeamDaysOff && second.isTeamDaysOff) {
        // This should never happen, but let's make sure the sorter is stable just in case
        return 0;
    }

    // Display the special team days off row last
    if (first.isTeamDaysOff || second.isTeamDaysOff) {
        return first.isTeamDaysOff ? 1 : -1;
    }

    // Sort other rows alphabetically
    return localeIgnoreCaseComparer(first.teamMember?.displayName ?? "", second.teamMember?.displayName ?? "");
}
